import React from 'react'
import { useAsyncAbortable } from 'react-async-hook'
import { Field } from 'react-final-form'
import { Button, Modal, ModalBody, ModalHeader, UncontrolledTooltip } from 'reactstrap'

import FA from '@src/components/common/FontAwesomeIcon'
import Link from '@src/components/common/Link'
import CostValue from '@src/components/costs/common/CostValue'
import { IRegisterTableField } from '@src/components/register/tablefields/FieldTypes'
import GenericSelectDropdown from '@src/components/register/tablefields/GenericSelectDropdown'
import GenericContentTable, { ITableHeader } from '@src/components/table/GenericContentTable'
import useModal from '@src/hooks/useModal'
import useProject from '@src/hooks/useProject'
import useTouched from '@src/hooks/useTouched'
import useProjectWidget from '@src/hooks/useWidget'
import { commitmentLabel, commitmentValue } from '@src/logic/forms/SelectHelpers'
import { CommitmentsList } from '@src/logic/http/Api'
import * as Routes from '@src/logic/routing/routes'
import { localLongDate } from '@src/logic/utils/Date'
import { mutedNotSet, mutedValue } from '@src/logic/utils/ValueHelper'
import { Commitment, CommitmentLink } from '@src/types/costs'
import { CommitmentLinksDefinition } from '@src/types/metadata'

const NO_OPTIONS_MESSAGE = () => 'Add commitments to your widget'

const commitmentTableHeaders = (projectId: string): ITableHeader<Commitment>[] => ([
    {
        name: 'Commitment',
        accessor: 'commitmentNo',
        sortKey: 'commitment_no',
        sortable: true
    },
    {
        name: 'Name',
        sortKey: 'name',
        sortable: true,
        overrideRenderer: commitment => <Link to={Routes.projectCostsCommitmentTypeDetail(projectId, commitment.type, commitment.id)}>{commitment.name}</Link>
    },
    {
        name: 'Date',
        sortKey: 'date',
        sortable: true,
        overrideRenderer: commitment => commitment.date != null ? localLongDate(commitment.date) : mutedNotSet
    },
    {
        name: 'Other Party',
        sortKey: 'other_party',
        sortable: true,
        overrideRenderer: commitment => commitment.otherParty ? commitment.otherParty.name : mutedValue('(none)')
    },
    {
        name: 'Status',
        overrideRenderer: commitment => {
            if (commitment.commitmentItems.length === 0) return mutedValue('N/A')

            const firstItemStatus = commitment.commitmentItems[0].status
            return commitment.commitmentItems.every(c => c.status === firstItemStatus)
                ? firstItemStatus
                : <em>Mixed<FA id={`mixed-status-${commitment.id}`} icon="question-circle" className="ml-2" /><UncontrolledTooltip target={`mixed-status-${commitment.id}`}>Line items have different statuses.</UncontrolledTooltip></em>
        }
    },
    {
        name: 'Value',
        overrideRenderer: commitment => <CostValue value={commitment.currentValue} />
    },
    {
        name: 'Claimed',
        overrideRenderer: commitment => <CostValue value={commitment.currentClaimed} />
    },
    {
        name: 'Certified',
        overrideRenderer: commitment => <CostValue value={commitment.currentCertified} />
    },
    {
        name: 'Paid',
        overrideRenderer: commitment => <CostValue value={commitment.currentPaid} />
    }
])

export default function CommitmentLinkField(props: IRegisterTableField<CommitmentLinksDefinition>) {
    const value = props.row.cells[props.col.id] as CommitmentLink[] | null
    const [project] = useProject()
    const widget = useProjectWidget()
    const options = React.useMemo(() => (value ?? []).concat(widget.commitments.map<CommitmentLink>(x => ({ id: x.id, type: x.type, name: x.name }))), [widget.commitments, value])
    const displayCommitmentLinkModal = useModal(false, { destroyOnClose: true })
    const isTouched = useTouched(props.isFocused, [value])
    const tableHeaders = React.useMemo(() => commitmentTableHeaders(project.id), [project.id])

    const commitmentsAsync = useAsyncAbortable(async (abortSignal) => {
        const response = await CommitmentsList(project.id, `id: ${value.map(x => x.id).join(',')}`, undefined, undefined, undefined, { abortSignal })
        return response.data.commitments
    }, [], {
        executeOnMount: false,
        executeOnUpdate: false,
        initialState: s => ({ error: undefined, loading: false, result: [], status: 'not-requested' }),
        setLoading: s => ({ ...s, loading: true, status: 'loading' })
    })

    React.useEffect(() => {
        if (displayCommitmentLinkModal.modalProps.isOpen) commitmentsAsync.execute()
    }, [displayCommitmentLinkModal.modalProps.isOpen])

    function handleOpenTable(e: React.MouseEvent) {
        e.stopPropagation()
        displayCommitmentLinkModal.actions.show()
    }

    return (
        <>
            {isTouched
                ? (
                    <Field name={`cells.r-${props.row.id}.c-${props.col.id}`} initialValue={value} validateFields={[]}>
                        {({ input }) =>
                            <>
                                {props.isFocused
                                    ? <>
                                        <GenericSelectDropdown
                                            {...props}
                                            value={input.value}
                                            onChange={input.onChange}
                                            getOptionLabel={commitmentLabel}
                                            getOptionValue={commitmentValue}
                                            options={options}
                                            noOptionsMessage={NO_OPTIONS_MESSAGE}
                                        />
                                    </>
                                    : (
                                        <span className="register__select-span d-inline-flex overflow-hidden align-items-center">
                                            {input.value?.length
                                                ? <>
                                                    <span className="text-truncate flex-grow-1">{input.value[0].name}</span>
                                                    <span className="register__link-button-span">
                                                        <Button color="link" className="register__external-link-button" onClick={handleOpenTable}>
                                                            <FA icon="external-link-alt" />
                                                        </Button>
                                                    </span>
                                                </>
                                                : <span className="register__placeholder" />
                                            }
                                        </span>
                                    )}
                            </>
                        }
                    </Field>
                )
                : (
                    <span className="register__select-span d-inline-flex overflow-hidden align-items-center">
                        {value?.length
                            ? <>
                                <span className="text-truncate flex-grow-1">{value[0].name}</span>
                                <span className="register__link-button-span">
                                    <Button color="link" className="register__external-link-button" onClick={handleOpenTable}>
                                        <FA icon="external-link-alt" />
                                    </Button>
                                </span>
                            </>
                            : <span className="register__placeholder" />
                        }
                    </span>
                )
            }
            <Modal {...displayCommitmentLinkModal.modalProps} size="xl">
                <ModalHeader toggle={displayCommitmentLinkModal.modalProps.toggle}>Linked Commitments</ModalHeader>
                <ModalBody>
                    <GenericContentTable
                        data={commitmentsAsync.result ?? []}
                        headers={tableHeaders}
                    />
                </ModalBody>
            </Modal>
        </>
    )
}

export function CommitmentLinkFieldFooter(props: IRegisterTableField<CommitmentLinksDefinition>) {
    const widget = useProjectWidget()
    const options = React.useMemo(() => widget.commitments.map<CommitmentLink>(x => ({ id: x.id, type: x.type, name: x.name })), [widget.commitments])

    return (
        <Field<CommitmentLink> name={`cells.c-${props.col.id}`}>
            {({ input }) =>
                <GenericSelectDropdown<CommitmentLinksDefinition, CommitmentLink>
                    {...props}
                    isFocused={true}
                    onChange={input.onChange}
                    value={input.value}
                    getOptionLabel={commitmentLabel}
                    getOptionValue={commitmentValue}
                    options={options}
                />
            }
        </Field>
    )
}
